/**
 * BtwInput — push-side interjection input (UI-6).
 *
 * Lets the user push a quick note into a running agent without
 * interrupting it. NOTE: the backend consumer that injects the note as a
 * HumanMessage does not exist yet — `onSend` currently returns false and
 * surfaces an honest "not available" message, so no phantom "sent" state is
 * flashed. Visible only while a deep run is active so it doesn't clutter the
 * panel when nothing is happening.
 */
import { useEffect, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import { Pin, SendHorizonal } from "@/components/icons"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"

interface BtwInputProps {
  /**
   * Returns true when the note was successfully dispatched. We only flash
   * the "sent" indicator on a true return so the user isn't lied to when
   * the WS is disconnected.
   */
  onSend: (note: string) => boolean
  /** Hide entirely when false — used by parents to gate on "run active". */
  enabled: boolean
}

export function BtwInput({ onSend, enabled }: BtwInputProps) {
  const { t } = useTranslation()
  const [value, setValue] = useState("")
  const [recentlySent, setRecentlySent] = useState<string | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const flashTimer = useRef<number | null>(null)

  useEffect(() => {
    return () => {
      if (flashTimer.current != null) {
        window.clearTimeout(flashTimer.current)
      }
    }
  }, [])

  if (!enabled) return null

  function handleSubmit() {
    const trimmed = value.trim()
    if (trimmed.length === 0) return
    const ok = onSend(trimmed)
    if (!ok) return
    setValue("")
    setRecentlySent(trimmed)
    // Auto-resize textarea back to single row.
    const ta = textareaRef.current
    if (ta) ta.style.height = "auto"
    if (flashTimer.current != null) window.clearTimeout(flashTimer.current)
    flashTimer.current = window.setTimeout(() => setRecentlySent(null), 4000)
  }

  return (
    <div className="border-border bg-muted/20 shrink-0 border-t px-3 py-2">
      <div className="flex items-start gap-2">
        <Textarea
          ref={textareaRef}
          className="min-h-0 flex-1 resize-none overflow-hidden px-2 py-1 text-xs leading-snug md:text-xs"
          rows={1}
          value={value}
          aria-label={t("deepExplorer.btw_aria", {
            defaultValue: "Send a side note to the running agent",
          })}
          placeholder={t("deepExplorer.btw_placeholder", {
            defaultValue: "Note to agent — added on the next step",
          })}
          onChange={(e) => {
            setValue(e.target.value)
            const ta = e.currentTarget
            ta.style.height = "auto"
            ta.style.height = `${Math.min(ta.scrollHeight, 96)}px`
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault()
              handleSubmit()
            }
          }}
        />
        <button
          aria-label={t("deepExplorer.btw_send", { defaultValue: "Send note" })}
          disabled={value.trim().length === 0}
          type="button"
          className={cn(
            "text-muted-foreground hover:text-primary shrink-0 rounded p-1 transition-colors",
            "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
            value.trim().length === 0 && "opacity-50"
          )}
          onClick={handleSubmit}
        >
          <SendHorizonal className="size-4" />
        </button>
      </div>
      {recentlySent && (
        <div className="text-muted-foreground mt-1 flex items-center gap-1 text-[10px]">
          <Pin className="size-2.5" />
          <span className="truncate">
            {t("deepExplorer.btw_applied", {
              note: recentlySent,
              defaultValue: `Note "${recentlySent}" will be applied on the next step.`,
            })}
          </span>
        </div>
      )}
    </div>
  )
}
